"use client";

import { useQuery } from "@tanstack/react-query";
import { useProjectConfigs } from "@/features/project/ProjectContext";
import { apiFetch } from "@/lib/api";
import type { ItemCollection } from "@/types";
import { orderByParam, useItemsOrder } from "./useItemsOrder";
import { useItemsScope, scopeParams, type ItemsScope } from "./useItemsScope";
import { usePage } from "./usePage";

/** Matches the backend's `ListFeaturesQuerySchema` default. */
const DEFAULT_ITEMS_LIMIT = 20;

interface ItemsQueryState extends ItemsScope {
  limit: number;
  offset: number;
  orderBy: string | undefined;
}

/**
 * The single source of items for the map, the result list and the count.
 *
 * The legacy app ran two queries with different keys — one keyed on the page for
 * the list, one without for the total — which meant two round trips for the same
 * filter state. The REST response carries `numberMatched` alongside the page of
 * features, so one query answers both: `useItems` and `useItemsInfos` share this
 * key and react-query dedupes them into a single request.
 */
function useItemsQueryParams(): ItemsQueryState {
  const configs = useProjectConfigs();
  const scope = useItemsScope();
  const { order } = useItemsOrder();
  const [page] = usePage();

  const limit = configs.itemsLimit ?? DEFAULT_ITEMS_LIMIT;

  return {
    ...scope,
    limit,
    offset: limit * (page - 1),
    orderBy: orderByParam(order),
  };
}

export function itemsQueryKey({ isReady: _isReady, ...params }: ItemsQueryState) {
  return ["items", params] as const;
}

function useItemsCollectionQuery() {
  const params = useItemsQueryParams();
  const { projectId, collectionId, limit, offset, orderBy, lang, isReady } = params;

  return useQuery({
    queryKey: itemsQueryKey(params),
    queryFn: ({ signal }) =>
      apiFetch<ItemCollection>(
        `/projects/${projectId}/collections/${collectionId}/items`,
        {
          params: { ...scopeParams(params), limit, offset, orderBy },
          lang,
          signal,
        }
      ),
    enabled: !!projectId && !!collectionId && isReady,
    // Keep the previous page on screen while the next one loads, instead of
    // blanking the list and the map.
    placeholderData: (previous) => previous,
  });
}

/** The current page of items. */
export function useItems() {
  return useItemsCollectionQuery();
}

/** How many items match the current filters, and the page size. */
export function useItemsInfos() {
  const query = useItemsCollectionQuery();
  const limit = useItemsQueryParams().limit;

  return {
    ...query,
    count: query.data?.numberMatched ?? 0,
    limit,
    pageCount: Math.ceil((query.data?.numberMatched ?? 0) / limit),
  };
}
